import { api } from "./baseApi";

export interface IpRequest {
	id: string;
	orgName: string;
	contactName: string;
	contactEmail: string;
	requesterType: "institution" | "vendor";
	vendorName: string;
	vendorWebsite: string;
	siteDomain: string;
	ipAddresses: string;
	userAgents: string;
	reason: string;
	submitterIp: string;
	submitterUa: string;
	status: "pending" | "whitelisted" | "declined";
	note: string;
	internalNote: string;
	questionsJson: string;
	reviewedBy: string;
	reviewedAt: string | null;
	createdAt: string;
	updatedAt: string;
	previousCount?: number;
}

export interface IpRequestQuestion {
	question: string;
	askedBy: string;
	askedAt: string;
}

export interface IpRequestListResult {
	requests: IpRequest[];
	pendingCount: number;
}

export interface EmailTemplate {
	subject: string;
	body: string;
}

export interface IpRequestEmailTemplates {
	confirmation: EmailTemplate;
	approved: EmailTemplate;
	declined: EmailTemplate;
	question: EmailTemplate;
}

export interface IpRequestTemplatesResult {
	templates: IpRequestEmailTemplates;
	defaults: IpRequestEmailTemplates;
	variables: { token: string; description: string }[];
	emailConfigured: boolean;
}

export const ipRequestApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getIpRequestPendingCount: builder.query<{ pendingCount: number }, void>({
			query: () => "/ip-requests/pending-count",
			providesTags: ["IpRequest"]
		}),
		getIpRequests: builder.query<IpRequestListResult, { status?: string }>({
			query: ({ status }) =>
				`/ip-requests${status && status !== "all" ? `?status=${status}` : ""}`,
			providesTags: ["IpRequest"]
		}),
		updateIpRequest: builder.mutation<
			IpRequest & { emailQueued?: boolean },
			{
				id: string;
				status?: string;
				note?: string;
				internalNote?: string;
				silent?: boolean;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `/ip-requests/${id}`,
				method: "PATCH",
				body
			}),
			invalidatesTags: ["IpRequest"]
		}),
		askIpRequestQuestion: builder.mutation<
			IpRequest,
			{ id: string; question: string }
		>({
			query: ({ id, question }) => ({
				url: `/ip-requests/${id}/question`,
				method: "POST",
				body: { question }
			}),
			invalidatesTags: ["IpRequest"]
		}),
		getIpRequestsByEmail: builder.query<
			{ requests: IpRequest[] },
			{ email: string; excludeId?: string }
		>({
			query: ({ email, excludeId }) => {
				const params = new URLSearchParams({ email });
				if (excludeId) params.set("excludeId", excludeId);
				return `/ip-requests/by-email?${params.toString()}`;
			},
			providesTags: ["IpRequest"]
		}),
		deleteIpRequest: builder.mutation<{ ok: boolean }, string>({
			query: (id) => ({ url: `/ip-requests/${id}`, method: "DELETE" }),
			invalidatesTags: ["IpRequest"]
		}),
		getIpRequestTemplates: builder.query<IpRequestTemplatesResult, void>({
			query: () => "/ip-requests/templates"
		}),
		updateIpRequestTemplates: builder.mutation<
			{ ok: boolean },
			IpRequestEmailTemplates
		>({
			query: (body) => ({
				url: "/ip-requests/templates",
				method: "PUT",
				body
			})
		})
	})
});

export const {
	useGetIpRequestsQuery,
	useGetIpRequestPendingCountQuery,
	useGetIpRequestsByEmailQuery,
	useUpdateIpRequestMutation,
	useAskIpRequestQuestionMutation,
	useDeleteIpRequestMutation,
	useGetIpRequestTemplatesQuery,
	useUpdateIpRequestTemplatesMutation
} = ipRequestApi;
